import Keycloak from 'keycloak-js';
import { computed, ref } from 'vue';
import { env } from '@/shared/config/env';

const keycloak = new Keycloak({
  url: env.oidc.authority,
  realm: env.oidc.realm,
  clientId: env.oidc.clientId
});

const token = ref<string | undefined>(undefined);
const isAuthenticated = ref(false);

/**
 * La session usurpée, quand il y en a une.
 *
 * Keycloak n'en sait rien : le jeton vient d'un échange fait par le backend, l'instance
 * `keycloak` n'est jamais initialisée dans ce cas, et c'est l'adresse retenue ici qui
 * permet au bandeau de dire de qui il s'agit. Rien n'est écrit dans le stockage du
 * navigateur : une session usurpée ne doit pas survivre à la fermeture de l'onglet.
 */
const impersonatedEmail = ref<string | undefined>(undefined);
const isImpersonating = computed(() => impersonatedEmail.value !== undefined);

/**
 * One in-flight refresh shared by every caller. The periodic timer, the 401
 * handler and `onTokenExpired` can fire within milliseconds of each other; each
 * asking Keycloak for its own refresh would race, and the losers would invalidate
 * the refresh token the winner just used.
 */
let refreshPromise: Promise<boolean> | null = null;

function refreshAccessToken(minValidity = 30): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = keycloak
    .updateToken(minValidity)
    .then((refreshed) => {
      if (refreshed) token.value = keycloak.token;
      return refreshed;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Logs the user in, then hands control back. `login-required` rather than
 * `check-sso`: every screen of this application needs an identity, so an
 * anonymous render would only ever be a flash of an empty shell.
 *
 * **Et c'est ce qui tient la promesse « un lien, deux écrans ».** L'invité qui arrive sur
 * `/bienvenue/{id}` vient de terminer le parcours d'action de Keycloak, qui a posé le cookie
 * de session sur son propre domaine : `login-required` se résout donc en silence, par une
 * redirection que la personne ne voit pas, sans second formulaire de connexion. Passer à
 * `check-sso` ou ajouter un écran intermédiaire casserait exactement ce que le chantier
 * d'invitation a cherché à obtenir — le service client comptait les écrans.
 */
/**
 * S'installe avec le jeton d'une session usurpée, sans passer par Keycloak.
 *
 * Il n'y a rien à initialiser : le jeton est déjà là, et keycloak-js n'a ni la session ni
 * le jeton de rafraîchissement qui iraient avec. C'est aussi pourquoi cette session ne se
 * renouvelle pas — elle dure ce que dure le jeton, puis l'API répond 401. Un diagnostic
 * n'a pas besoin de plus, et une usurpation qui se renouvelle toute seule est une
 * usurpation qu'on oublie ouverte.
 */
function startImpersonation(accessToken: string, email: string): void {
  token.value = accessToken;
  impersonatedEmail.value = email;
  isAuthenticated.value = true;
}

/**
 * Sort de la session usurpée.
 *
 * En rechargeant sur l'adresse nue : le jeton n'est qu'en mémoire, donc le recharger suffit
 * à le perdre, et l'application repart sur le chemin normal — c'est-à-dire la connexion de
 * la personne qui est vraiment devant l'écran.
 */
function stopImpersonation(): void {
  token.value = undefined;
  impersonatedEmail.value = undefined;
  isAuthenticated.value = false;
  window.location.replace(window.location.origin);
}

/**
 * Efface de l'adresse les paramètres OIDC que `keycloak-js` n'a pas demandés.
 *
 * **Le cas concret** : le lien d'invitation termine son parcours dans Keycloak, qui nous
 * renvoie sur `/bienvenue/{id}?code=…&session_state=…&iss=…`. `keycloak-js` n'a lancé aucune
 * requête, donc il ignore ces paramètres — puis il repart se connecter en passant l'adresse
 * courante, salissures comprises, comme `redirect_uri`. Keycloak refuse alors cette URI :
 * une adresse de retour ne peut pas porter elle-même des paramètres de réponse OIDC. Vérifié
 * au curl — la même adresse sans eux est acceptée, avec eux elle rend 400.
 *
 * **Pourquoi c'est sûr** : avant de partir, `keycloak-js` dépose `kc-callback-<state>`. Si
 * cette trace existe, le retour est le sien et on ne touche à rien. Sans elle, ces paramètres
 * viennent d'ailleurs et n'ont rien à faire dans l'adresse.
 */
function effacerUnRetourOidcEtranger(): void {
  const params = new URLSearchParams(window.location.search);
  // `error` compte autant que les autres : un retour en échec — `?error=access_denied&state=…`
  // — salit l'adresse exactement pareil, et on l'efface plus bas. L'omettre ici laissait ce
  // cas-là intact.
  const retour = ['code', 'session_state', 'iss', 'error'];
  if (!retour.some((p) => params.has(p))) return;

  const state = params.get('state');
  if (state && sien(`kc-callback-${state}`)) return;

  [...retour, 'state', 'error_description'].forEach((p) => params.delete(p));
  const reste = params.toString();
  // `window.history.state` et non `{}` : Vue Router range là sa position de défilement et de
  // quoi revenir en arrière. L'écraser ne se voit pas tout de suite — c'est le retour arrière
  // qui se met à sauter, une fois, plus tard.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + (reste ? `?${reste}` : '') + window.location.hash
  );
}

/** La trace laissée par `keycloak-js`, où qu'il l'ait rangée. */
function sien(cle: string): boolean {
  try {
    if (window.localStorage.getItem(cle) !== null) return true;
    if (window.sessionStorage.getItem(cle) !== null) return true;
  } catch {
    // Stockage refusé (navigation privée, cookies bloqués) : on ne peut rien affirmer.
    // Ne rien effacer est le choix prudent — un retour légitime doit passer.
    return true;
  }
  return document.cookie.includes(cle);
}

async function requireUserLogin(onAuthenticated: () => void): Promise<void> {
  effacerUnRetourOidcEtranger();

  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256'
  });

  isAuthenticated.value = authenticated;
  token.value = keycloak.token;

  keycloak.onTokenExpired = () => {
    void refreshAccessToken(-1);
  };

  onAuthenticated();
}

export function useAuthentication() {
  return {
    keycloak,
    token: computed(() => token.value),
    isAuthenticated: computed(() => isAuthenticated.value),
    requireUserLogin,
    refreshAccessToken,
    isImpersonating,
    impersonatedEmail: computed(() => impersonatedEmail.value),
    startImpersonation,
    stopImpersonation,
    logout: () => keycloak.logout({ redirectUri: window.location.origin })
  };
}
